import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Wrench, Clock, RefreshCw, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MaintenancePage = () => {
  const navigate = useNavigate();
  const [maintenanceData, setMaintenanceData] = useState({
    maintenance_mode: true,
    maintenance_message: null,
    maintenance_end_time: null
  });
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    fetchMaintenanceStatus();
  }, []);

  const fetchMaintenanceStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/site-settings/public/maintenance`);
      setMaintenanceData(response.data);
      
      // If maintenance mode is off, redirect to home
      if (!response.data.maintenance_mode) {
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to fetch maintenance status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckAgain = async () => {
    setChecking(true);
    await fetchMaintenanceStatus();
    setChecking(false);
  };

  const formatEndTime = (isoString) => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return null;
    }
  };

  const formattedEndTime = formatEndTime(maintenanceData.maintenance_end_time);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50 flex items-center justify-center">
        <div className="animate-spin">
          <Wrench className="w-8 h-8 text-orange-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-xl border-2 border-orange-200">
        <CardContent className="p-8 text-center">
          {/* Icon */}
          <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Wrench className="w-10 h-10 text-orange-600" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            We&apos;ll Be Right Back
          </h1>

          {/* Message */}
          <p className="text-gray-600 mb-6">
            {maintenanceData.maintenance_message || 
              "Our site is currently undergoing scheduled maintenance. We'll be back online shortly."}
          </p>

          {/* Estimated End Time */}
          {formattedEndTime && (
            <div className="flex items-center justify-center gap-2 p-3 bg-orange-50 rounded-lg mb-6">
              <Clock className="w-5 h-5 text-orange-600" />
              <span className="text-sm text-orange-800">
                Expected back: <strong>{formattedEndTime}</strong>
              </span>
            </div>
          )}

          {/* Check Again Button */}
          <Button
            onClick={handleCheckAgain}
            variant="outline"
            className="w-full mb-4"
            disabled={checking}
          >
            {checking ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Checking...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Check if we&apos;re back
              </>
            )}
          </Button>

          {/* Admin Login Link */}
          <div className="pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 mb-2">
              Administrator?
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/profile')}
              className="text-gray-600 hover:text-gray-900"
            >
              <Shield className="w-4 h-4 mr-1" />
              Admin Login
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MaintenancePage;
